import { useMutation } from "@tanstack/react-query";
import { Check, ChevronDown, Copy, Plus } from "lucide-react";
import { type SubmitEvent, useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@lawline/ui/components/dialog";
import { Input } from "@lawline/ui/components/input";
import { Label } from "@lawline/ui/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@lawline/ui/components/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@lawline/ui/components/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@lawline/ui/components/tooltip";

import { useAuth } from "@platform/auth/context";
import { createKeyMutation } from "../api-keys.mutations";
import type { CreatedApiKey } from "../api-keys.types";

// The empty value issues a key that does not expire. A key that dies on a date nobody watches
// takes the customer's integration with it.
const NEVER = "never";

const EXPIRY_OPTIONS = [
	{ key: "days30", value: "30" },
	{ key: "days90", value: "90" },
	{ key: "year1", value: "365" },
	{ key: NEVER, value: NEVER },
] as const;

const SCOPE_OPTIONS = ["v1", "mcp"] as const;

// On success the raw secret replaces the form in place. The server returns that
// secret once, so it lives in dialog state and never in the cached list.
export function CreateApiKey() {
	const t = useTranslations("apiKeys.create");
	const { can } = useAuth();
	const create = useMutation(createKeyMutation);
	const [open, setOpen] = useState(false);
	const [name, setName] = useState("");
	const [expiry, setExpiry] = useState("90");
	const [scopes, setScopes] = useState<string[]>([...SCOPE_OPTIONS]);
	const [created, setCreated] = useState<CreatedApiKey | null>(null);
	const [copied, setCopied] = useState(false);

	function onOpenChange(next: boolean) {
		setOpen(next);
		if (!next) {
			setName("");
			setExpiry("90");
			setScopes([...SCOPE_OPTIONS]);
			create.reset();
			setCreated(null);
			setCopied(false);
		}
	}

	function submit(e: SubmitEvent) {
		e.preventDefault();
		const trimmed = name.trim();
		if (!trimmed) return;
		create.mutate(
			{ name: trimmed, ttlDays: expiry === NEVER ? null : Number(expiry), scopes },
			{ onSuccess: setCreated },
		);
	}

	if (!can({ apiKey: ["create"] })) return null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger asChild>
				<Button size="sm">
					<Plus className="size-4" />
					{t("button")}
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				{created ? (
					<>
						<DialogHeader>
							<DialogTitle>{t("successTitle")}</DialogTitle>
						</DialogHeader>
						<div className="bg-muted flex items-center gap-1 rounded-md border py-1.5 pr-2 pl-2">
							<code className="flex-1 font-mono text-xs leading-relaxed break-all">
								{created.raw}
							</code>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className="size-5 shrink-0"
										onClick={() => {
											navigator.clipboard.writeText(created.raw);
											setCopied(true);
										}}
									>
										{copied ? (
											<Check className="text-primary size-4" />
										) : (
											<Copy className="size-4" />
										)}
									</Button>
								</TooltipTrigger>
								<TooltipContent>{t("copy")}</TooltipContent>
							</Tooltip>
						</div>
						<DialogFooter>
							<Button onClick={() => onOpenChange(false)}>{t("done")}</Button>
						</DialogFooter>
					</>
				) : (
					<>
						<DialogHeader>
							<DialogTitle>{t("title")}</DialogTitle>
						</DialogHeader>
						<form onSubmit={submit} className="flex flex-col gap-4">
							<div className="flex flex-col gap-2">
								<Label htmlFor="key-name">{t("nameLabel")}</Label>
								<Input
									id="key-name"
									placeholder=""
									value={name}
									onChange={(e) => setName(e.target.value)}
									maxLength={100}
									required
								/>
							</div>

							<div className="flex gap-4">
								<div className="flex flex-1 flex-col gap-2">
									<Label htmlFor="key-expiry">{t("expiresLabel")}</Label>
									<Select value={expiry} onValueChange={setExpiry}>
										<SelectTrigger id="key-expiry">
											<SelectValue />
										</SelectTrigger>
										<SelectContent position="popper">
											{EXPIRY_OPTIONS.map((o) => (
												<SelectItem key={o.value} value={o.value}>
													{t(`expiryOptions.${o.key}`)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="flex flex-1 flex-col gap-2">
									<Label htmlFor="key-scope">{t("scopeLabel")}</Label>
									<Popover modal>
										<PopoverTrigger asChild>
											<Button
												id="key-scope"
												type="button"
												variant="outline"
												className="justify-between font-normal"
											>
												{SCOPE_OPTIONS.filter((s) => scopes.includes(s))
													.map((s) => t(`scopeOptions.${s}`))
													.join(", ")}
												<ChevronDown className="size-4 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-1">
											{SCOPE_OPTIONS.map((s) => {
												const active = scopes.includes(s);
												// keep at least one scope: backend rejects an empty set
												const locked = active && scopes.length === 1;
												return (
													<button
														key={s}
														type="button"
														disabled={locked}
														onClick={() =>
															setScopes((prev) =>
																active ? prev.filter((p) => p !== s) : [...prev, s],
															)
														}
														className="hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm disabled:pointer-events-none disabled:opacity-50"
													>
														<Check className={`size-4 ${active ? "opacity-100" : "opacity-0"}`} />
														{t(`scopeOptions.${s}`)}
													</button>
												);
											})}
										</PopoverContent>
									</Popover>
								</div>
							</div>

							{create.error && (
								<p className="text-destructive text-sm">{create.error.message || t("error")}</p>
							)}

							<DialogFooter>
								<Button type="submit" disabled={create.isPending || !name.trim()}>
									{create.isPending ? t("submitLoading") : t("submit")}
								</Button>
							</DialogFooter>
						</form>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
